import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { SEO } from "@/components/SEO";
import { ScheduledStreamsCard } from "@/components/fan/ScheduledStreamsCard";
import { 
  Play, 
  Search, 
  MessageCircle, 
  Wallet, 
  User, 
  Radio,
  BarChart3,
  ShieldCheck,
  Compass,
  Gift,
  FolderOpen,
  Download
} from "lucide-react";

const Home = () => {
  const { user, userRole } = useAuth();

  const userFeatures = [
    {
      title: "Discover",
      description: "Explore live streams and recordings",
      icon: Compass,
      href: "/discovery",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      title: "Search",
      description: "Find creators and connect with fans",
      icon: Search,
      href: "/search",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      title: "Install App",
      description: "Install HiStage as a mobile app",
      icon: Download,
      href: "/install",
      gradient: "from-blue-600 to-indigo-600"
    },
    {
      title: "Referrals",
      description: "Invite friends and earn rewards",
      icon: Gift,
      href: "/referrals",
      gradient: "from-emerald-500 to-teal-500"
    },
    {
      title: "Messages",
      description: "Chat with other users",
      icon: MessageCircle,
      href: "/messages",
      gradient: "from-green-500 to-emerald-500"
    },
    {
      title: "Wallet",
      description: "Manage your StagCoins",
      icon: Wallet,
      href: "/wallet",
      gradient: "from-yellow-500 to-orange-500"
    },
    {
      title: "My Profile",
      description: "View and edit your profile",
      icon: User,
      href: "/profile",
      gradient: "from-red-500 to-pink-500"
    }
  ];

  const creatorFeatures = [
    {
      title: "Go Live",
      description: "Start streaming to your audience",
      icon: Radio,
      href: "/go-live",
      gradient: "from-red-500 to-rose-500"
    },
    {
      title: "Creator Dashboard",
      description: "Manage streams and analytics",
      icon: BarChart3,
      href: "/creator",
      gradient: "from-indigo-500 to-purple-500"
    },
    {
      title: "Content Management",
      description: "Upload and manage your content",
      icon: FolderOpen,
      href: "/creator?tab=content",
      gradient: "from-violet-500 to-indigo-500"
    }
  ];

  const adminFeatures = [
    {
      title: "Analytics",
      description: "View platform analytics",
      icon: BarChart3,
      href: "/analytics",
      gradient: "from-violet-500 to-purple-500"
    },
    {
      title: "Admin Panel",
      description: "Manage platform settings",
      icon: ShieldCheck,
      href: "/admin",
      gradient: "from-slate-500 to-gray-500"
    }
  ];

  return (
    <>
      <SEO 
        title="Home - HiStage"
        description="Your personal HiStage dashboard"
      />
      
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-6 py-12">
          {/* Welcome Section */}
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Welcome to HiStage
            </h1>
            <p className="text-xl text-muted-foreground">
              Your fan-driven live stage experience
            </p>
          </div>

          {/* Main Features Grid */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6">Explore Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {userFeatures.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Link key={feature.href} to={feature.href}>
                    <Card className="hover:shadow-lg transition-all hover:-translate-y-1 h-full cursor-pointer">
                      <CardHeader>
                        <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4`}>
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <CardTitle>{feature.title}</CardTitle>
                        <CardDescription>{feature.description}</CardDescription>
                      </CardHeader>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </section>

          {/* Upcoming Streams Widget */}
          {user && (
            <section className="mb-12">
              <ScheduledStreamsCard />
            </section>
          )}

          {/* Creator Features */}
          {userRole === 'creator' && (
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-6">Creator Tools</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {creatorFeatures.map((feature) => {
                  const Icon = feature.icon;
                  return (
                    <Link key={feature.href} to={feature.href}>
                      <Card className="hover:shadow-lg transition-all hover:-translate-y-1 h-full cursor-pointer">
                        <CardHeader>
                          <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4`}>
                            <Icon className="h-6 w-6 text-white" />
                          </div>
                          <CardTitle>{feature.title}</CardTitle>
                          <CardDescription>{feature.description}</CardDescription>
                        </CardHeader>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* Admin Features */}
          {userRole === 'admin' && (
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-6">Admin Tools</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {adminFeatures.map((feature) => {
                  const Icon = feature.icon;
                  return (
                    <Link key={feature.href} to={feature.href}>
                      <Card className="hover:shadow-lg transition-all hover:-translate-y-1 h-full cursor-pointer">
                        <CardHeader>
                          <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4`}>
                            <Icon className="h-6 w-6 text-white" />
                          </div>
                          <CardTitle>{feature.title}</CardTitle>
                          <CardDescription>{feature.description}</CardDescription>
                        </CardHeader>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export default Home;
